import React from "react";
import { motion } from "framer-motion";
import { useSite } from "@/context/ThemeContext";
import { Reveal } from "@/components/Reveal";
import { getIcon } from "@/lib/icons";
import { Play } from "lucide-react";


export default function About() {
  const { data } = useSite();
  if (!data) return null;
  const about = data.content.about;
  const team = data.team || [];

  return (
    <div className="relative z-10 pt-28">
      {/* Story + intro */}
      <section className="mx-auto max-w-7xl px-5 py-12" data-testid="about-story">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <span className="text-xs uppercase tracking-[0.25em] font-semibold text-primary-c">About Us</span>
              <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter text-t mt-4">
                Training for Change <span className="text-primary-c">since 1999.</span>
              </h1>
              <p className="text-muted-c text-lg leading-relaxed mt-7 whitespace-pre-line">{about.story}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <div className="glass rounded-3xl p-3 card-hover">
                <div className="relative rounded-2xl overflow-hidden">
                  <video src="" controls playsInline preload="metadata"
                    className="w-full h-full rounded-2xl aspect-video object-cover bg-black" data-testid="about-video" />
                </div>
                <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-c">
                  <Play size={15} className="text-primary-c" /> Take a walkthrough of our campus &amp; labs
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Campus gallery */}
      {(about.gallery || []).length > 0 && (
        <section className="mx-auto max-w-7xl px-5 py-10" data-testid="about-gallery">
          <Reveal><h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-t mb-8">Inside Our Campus</h2></Reveal>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            {about.gallery.map((src, i) => (
              <Reveal key={i} delay={(i % 4) * 0.08}>
                <div className={`rounded-2xl overflow-hidden glass card-hover ${i === 0 ? "col-span-2 row-span-2" : ""}`}>
                  <img src={src} alt={`Campus ${i + 1}`} loading="lazy" className="w-full h-full object-cover aspect-video" />
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Timeline */}
      <section className="py-16" data-testid="about-timeline">
        <div className="mx-auto max-w-7xl px-5">
          <Reveal><h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-t">Our Journey</h2>
          <p className="text-muted-c mt-3 text-lg">From 1999 to today — milestones that keep growing.</p></Reveal>
        </div>
        <div className="mt-10 overflow-x-auto hide-scrollbar">
          <div className="flex gap-6 px-5 w-max pb-4">
            {about.timeline.map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="glass card-hover rounded-2xl p-7 w-[260px] shrink-0 relative" data-testid={`timeline-${t.year}`}>
                <div className="font-display font-black text-4xl text-primary-c">{t.year}</div>
                <div className="h-px w-full my-4" style={{ background: "var(--border)" }} />
                <h3 className="font-display font-bold text-lg text-t">{t.title}</h3>
                <p className="text-muted-c text-sm mt-2">{t.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="mx-auto max-w-7xl px-5 py-16" data-testid="about-philosophy">
        <Reveal><h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-t mb-10">Our Philosophy</h2></Reveal>
        <div className="grid gap-5 md:grid-cols-3">
          {about.philosophy.map((p, i) => {
            const Icon = getIcon(p.icon);
            return (
              <Reveal key={i} delay={i * 0.1}>
                <div className="glass card-hover rounded-2xl p-8 h-full">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6" style={{ background: "rgba(var(--primary-rgb),0.12)" }}>
                    <Icon className="text-primary-c" size={28} />
                  </div>
                  <p className="font-display font-bold text-xl sm:text-2xl text-t leading-snug">{p.title}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Team */}
      <section className="mx-auto max-w-7xl px-5 py-16" data-testid="about-team">
        <Reveal><h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-t mb-3">Meet the Team</h2>
        <p className="text-muted-c text-lg mb-10">The people behind every learner's success.</p></Reveal>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {team.map((m, i) => (
            <Reveal key={m.id || i} delay={(i % 4) * 0.08}>
              <div className="glass card-hover rounded-2xl p-6 text-center" data-testid={`team-${i}`}>
                <div className="w-24 h-24 mx-auto rounded-full overflow-hidden mb-4 flex items-center justify-center" style={{ background: "rgba(var(--primary-rgb),0.12)" }}>
                  {m.imageUrl ? <img src={m.imageUrl} alt={m.name} className="w-full h-full object-cover" /> :
                    <span className="font-display font-black text-3xl text-primary-c">{(m.name || "B").charAt(0)}</span>}
                </div>
                <h3 className="font-display font-bold text-lg text-t">{m.name}</h3>
                <p className="text-sm text-primary-c mt-1">{m.designation}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
